import { useEffect, useMemo, useState } from 'react';

import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';
import { DateTime } from 'luxon';
import { Navigate, useParams } from 'react-router';

import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import { AssignmentManagementForm } from 'src/features/team/assignment-management/AssignmentManagementForm/AssignmentManagementForm';
import { BulkCancelTripModal } from 'src/features/trip/management/cancel/BulkCancelTripModal/BulkCancelTripModal';
import CancelSuggestionsModal from 'src/features/trip/management/cancel/CancelSuggestionsModal/CancelSuggestionsModal';
import CancelTripModal from 'src/features/trip/management/cancel/CancelTripModal/CancelTripModal';
import TripTagsModal from 'src/features/trip/management/TripTagModal';
import { convertLocalDaysToDaysOfWeek } from 'src/features/trip/routeTemplates/forms/daysOfWeekMaps';
import { routeTemplateActions } from 'src/features/trip/routeTemplates/routeTemplates.slice';
import TripStatusUpdatesModal from 'src/features/trip/status-update/TripStatusUpdatesModal';

import { useDispatchRepository } from '../common/useDispatchRepository';
import { DispatchCalendar } from '../DispatchCalendar';
import { useDispatchEventResources } from '../DispatchCalendar/useDispatchEventResources';
import { dispatcherSlice } from '../dispatcher.slice';
import { DispatchToolbar } from '../DispatchToolbar';
import AssignmentBreakModal from '../modals/AssignmentBreakModal/AssignmentBreakModal';
import { CallRequestModal } from '../modals/CallRequestModal';
import { ConfirmWillCallActivationModal } from '../modals/ConfirmWillCallActivationModal';
import { DeleteBreakModal } from '../modals/DeleteBreakModal';
import { TripAcceptRequestModal } from '../modals/requestedTrips/accept/TripAcceptRequestModal';
import { useAcceptTripRequestConfirmModalProps } from '../modals/requestedTrips/accept/useAcceptTripRequestConfirmModalProps';
import { TripRejectRequestModal } from '../modals/requestedTrips/reject/TripRejectRequestModal';
import { useRejectTripRequestConfirmModalProps } from '../modals/requestedTrips/reject/useRejectTripRequestConfirmModalProps';
import { RunBambiRunAssignmentSelectionStep } from '../modals/RunBambiRunAssignmentSelectionStep';
import { UnassignConfirmationModal } from '../modals/UnassignConfirmationModal';
import { MultiloadAssignmentFlow } from '../multi-loading/MultiloadAssignmentFlow';
import { AssignTripFlow } from './AssignTripFlow';
import { ApplyRouteTemplatesModal } from './UnassignedTrips/RouteTemplates/ApplyRouteTemplatesModal';
import { UnassignedTrips } from './UnassignedTrips/UnassignedTrips';

const ONE_MINUTE_IN_MS = 60000;

export function DispatchRoute() {
  const { selectedDate } = useParams();

  const dispatch = useAppDispatch();

  const selectedDateFromState = useAppSelector(
    (state) => state.dispatcher.selectedDate
  );
  const dispatchResponse = useAppSelector(
    (state) => state.dispatcher.dispatchResponse
  );
  const isDispatchSidebarOpen = useAppSelector(
    (state) => state.dispatcher.isDispatchSidebarOpen
  );
  const isMenuActionInProgress = useAppSelector(
    (state) => state.dispatcher.isMenuActionInProgress
  );
  const isRunBambiRunInProgress = useAppSelector(
    (state) => state.dispatcher.isRunBambiRunInProgress
  );
  const runBambiRunJobId = useAppSelector(
    (state) => state.dispatcher.runBambiRunJobId
  );
  const previewEvents = useAppSelector(
    (state) => state.dispatcher.previewEvents
  );
  const isDraggingEvent = useAppSelector(
    (state) => state.dispatcher.calendarEventDragState.isDragging
  );

  const [pollingInterval, setPollingInterval] = useState<number | undefined>(
    ONE_MINUTE_IN_MS
  );

  const { resources, invalids, events } =
    useDispatchEventResources(dispatchResponse);
  const isMultiloadAssignmentFlowOpen = useAppSelector(
    (state) =>
      state.dispatcher.multiloadAssignmentFlow.isMultiloadAssignmentFlowOpen
  );
  const modalToShow = useAppSelector(
    (state) => state.dispatcher.modals.modalToShow
  );
  const isAssignTripModalOpen = useAppSelector(
    (state) => state.dispatcher.tripAssignmentFlow.isAssignTripModalOpen
  );

  const allEvents = useMemo(
    () => [...events, ...(previewEvents || [])],
    [events, previewEvents]
  );

  const tripAcceptRequestModalProps = useAcceptTripRequestConfirmModalProps();
  const tripRejectRequestModalProps = useRejectTripRequestConfirmModalProps();

  const { isLoading: dispatchLoading } = useDispatchRepository({
    refetchOnMountOrArgChange: true,
    pollingInterval,
    skip:
      isMenuActionInProgress ||
      !selectedDate ||
      !!modalToShow ||
      isMultiloadAssignmentFlowOpen ||
      isAssignTripModalOpen,
  });

  useEffect(() => {
    dispatch(dispatcherSlice.actions.dispatchRouteInit());

    if (selectedDate) {
      dispatch(dispatcherSlice.actions.setSelectedDate(selectedDate));
      dispatch(
        routeTemplateActions.setFilterDaysOfWeek(
          convertLocalDaysToDaysOfWeek([
            DateTime.fromISO(selectedDate).toFormat('cccc'),
          ])
        )
      );
    }
  }, [dispatch, selectedDate]);

  useEffect(() => {
    if (previewEvents?.length || isDraggingEvent) {
      setPollingInterval(undefined);
    } else {
      setPollingInterval(ONE_MINUTE_IN_MS);
    }
  }, [previewEvents, isDraggingEvent]);

  if (!selectedDate) {
    // use the date from state when one is not in the url
    // Keeps the users selected date on page navigation
    return <Navigate to={`/dispatch/${selectedDateFromState}`} replace />;
  }

  if (isRunBambiRunInProgress && runBambiRunJobId) {
    return <Navigate to={`/dispatch/${selectedDate}/${runBambiRunJobId}`} />;
  }

  return (
    <>
      {dispatchLoading || isRunBambiRunInProgress ? (
        <div className="isolate flex h-[85vh] max-h-full max-w-full items-center justify-center">
          <LoadingIndicator />
        </div>
      ) : (
        <div className="grid sm:grid-cols-12">
          {isDispatchSidebarOpen && (
            <div className="col-span-3">
              <DispatchToolbar>
                <UnassignedTrips />
              </DispatchToolbar>
            </div>
          )}
          <div
            className={isDispatchSidebarOpen ? 'col-span-9 ' : 'col-span-12'}
          >
            <DispatchCalendar
              resources={resources}
              invalids={invalids}
              // Don't show events if no resources exists because it looks broken
              events={resources.length ? allEvents : []}
              dragAndDropConfig={{
                dragToMove: true,
                externalDrop: true,
                externalDrag: true,
              }}
            />
          </div>
          <AssignmentBreakModal />
          <AssignTripFlow />
          <CallRequestModal />
          <ConfirmWillCallActivationModal />
          <DeleteBreakModal />
          <UnassignConfirmationModal />
          <TripRejectRequestModal props={tripRejectRequestModalProps} />
          <TripAcceptRequestModal props={tripAcceptRequestModalProps} />
          <RunBambiRunAssignmentSelectionStep />
          <MultiloadAssignmentFlow />
          <AssignmentManagementForm hideCloneButton />
          <TripTagsModal />
          <CancelTripModal />
          <CancelSuggestionsModal />
          <TripStatusUpdatesModal />
          <BulkCancelTripModal />
          <ApplyRouteTemplatesModal />
        </div>
      )}
    </>
  );
}
